import { useCallback, useMemo, useState } from 'react';
import {
  OpenedElsewherePrompt,
  OpenedElsewherePromptProps,
  ShowOpenedElsewherePrompt,
  UseOpenedElsewherePromptProps,
} from './types';
import { errorHandler } from '80.quickConnect.Core/helpers';

const tag = '10.quickConnect.app/components/shared/Dialogs/OpenedElsewherePrompt/useOpenedElsewherePrompt.ts';

/**
 * Saisie ouverte dans un autre onglet : l'action proposée n'est exécutée qu'après confirmation de l'utilisateur, une seule
 * fois. Les popups sont rendues par OpenedElsewherePrompt avec promptProps.
 */
const useOpenedElsewherePrompt = (): UseOpenedElsewherePromptProps => {
  const [pending, setPending] = useState<{ prompt: OpenedElsewherePrompt; onRefused?: () => void }>();

  const showPrompt = useCallback<ShowOpenedElsewherePrompt>(
    (prompt, onRefused) => setPending({ prompt, onRefused }),
    [],
  );

  const confirm = useCallback(async () => {
    if (!pending) return;
    setPending(undefined);
    try {
      await pending.prompt.action();
    } catch (error) {
      errorHandler(tag, error, 'confirm', 'error');
    }
  }, [pending]);

  const refuse = useCallback(() => {
    const onRefused = pending?.onRefused;
    setPending(undefined);
    onRefused?.();
  }, [pending]);

  const promptProps = useMemo<OpenedElsewherePromptProps>(
    () => ({ prompt: pending?.prompt, confirm, refuse }),
    [pending, confirm, refuse],
  );

  return { showPrompt, promptProps };
};

export default useOpenedElsewherePrompt;
